<template>
	<div class="p-fluid p-formgrid p-grid p-input-filled p-input-filled">
		<div class="p-field p-col-6">
			<label for="company">{{$t('Squeeze.XmlMapper.Company')}} *</label>
			<InputText id="company" v-model.trim="v$.company.$model" :class="{'p-invalid':v$.company.$invalid && showErrorMessage}" @keyup="update" required="true" autofocus />
			<small v-if="v$.company.required.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.XmlMapper.Company')}) }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="creditor">{{$t('Squeeze.XmlMapper.Creditor')}} *</label>
			<InputText id="creditor" v-model.trim="v$.creditor.$model" :class="{'p-invalid':v$.creditor.$invalid && showErrorMessage}" @keyup="update" required="true" />
			<small v-if="(v$.creditor.$invalid && showErrorMessage)" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.XmlMapper.Creditor')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="documentclassid">{{$t('Squeeze.XmlMapper.DocumentClass')}} *</label>
			<Dropdown id="documentclassid" v-model="value.documentclassid" :options="documentClasses" :loading="loading" :class="{'p-invalid':v$.documentclassid.$invalid && showErrorMessage}" optionValue="id" :optionLabel="getTranslatedDescription" @change="updateDocumentClass"  />
			<small v-if="v$.documentclassid.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.XmlMapper.DocumentClass')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="columnName">{{$t('Squeeze.XmlMapper.ColumnName')}} *</label>
			<Dropdown id="columnName"
				v-model="value.columnName"
				:options="allColumnsOfTableLineItems"
				:loading="loading"
				:class="{'p-invalid':v$.documentclassid.$invalid && showErrorMessage}"
				optionValue="name"
				:optionLabel="getColumnDescription"
				@change="update"
				:filter="true"
				:autoFilterFocus="true"
				@show="showDropdownOverlay"
			/>
			<small v-if="v$.columnName.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.XmlMapper.ColumnName')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="xpath">{{$t('Squeeze.XmlMapper.XPath')}} *</label>
			<Textarea id="xpath" v-model.trim="v$.xpath.$model" :class="{'p-invalid':v$.xpath.$invalid && showErrorMessage}" @keyup="update" :autoResize="true" rows="5" required="true" style="width: 100%" />
			<small v-if="(v$.xpath.$invalid && showErrorMessage)" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.XmlMapper.XPath')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="active">{{$t('Squeeze.XmlMapper.Active')}}</label><br/>
			<Checkbox id="active" v-model="value.active" :binary="true" @change="update" />
		</div>
	</div>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, nextTick, onMounted, PropType, reactive, watch} from "vue";
import InputText from "primevue/inputtext";
import {DocumentClass, DocumentTableColumn, XmlColumnFieldTraining} from "@dex/squeeze-client-ts";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import Dropdown from "primevue/dropdown";
import {useI18n} from "vue-i18n";
import {showDropdownOverlay} from "@/util/StylesHelper";
import Textarea from "primevue/textarea";
import Checkbox from "primevue/checkbox";
import {FormState} from "@/util/Types";

type XmlColumnFieldTrainingFormState = FormState<XmlColumnFieldTraining, "company" | "creditor" | "documentclassid" | "xpath" | "columnName">;

export default defineComponent({
	name: "XmlMappingPositionForm",
	components: {
		InputText, Dropdown, Textarea, Checkbox,
	},
	props: {
		columnEntry: {
			type: Object as PropType<XmlColumnFieldTraining>,
			default: () => ({}),
		},
		documentClasses: {
			type: Array as PropType<DocumentClass[]>,
			default: () => [],
		},
		allColumnsOfTableLineItems: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update', 'onChangeDocumentClass'],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Entry-Data of the column field training */
		const value = reactive<XmlColumnFieldTrainingFormState>({
			company: '',
			creditor: '',
			documentclassid: '',
			xpath: '',
			columnName: '',
			readonly: false,
			active: true,
		});

		/** Determines the required rules for validation */
		const rules = {
			company: { required },
			creditor: { required },
			documentclassid: { required },
			xpath: { required },
			columnName: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.columnEntry);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.columnEntry, () => {
			Object.assign(value, props.columnEntry);
		})

		/** Triggered when input-field is changed */
		const update = async () => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			emit("update", value, v$.value.$invalid);
		}

		/** Triggered when a document class changes **/
		const updateDocumentClass = async (data: any)  => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			emit("onChangeDocumentClass", value, v$.value.$invalid, data.value);
		}

		/**
		 * Get the description of a column
		 * @param row
		 */
		const getColumnDescription = (row: any) => {
			if (props.allColumnsOfTableLineItems) {
				const columns = props.allColumnsOfTableLineItems.filter(column => column.name === row.name);

				if (columns.length > 1) {
					return row.name + " (" + getTranslatedDescription(columns[0]) + " (" + t('Squeeze.XmlMapper.FurtherEntries') +"))";
				} else if (columns.length === 1) {
					return row.name + " (" + getTranslatedDescription(columns[0]) + ")";
				}
			}

			return row.name;
		}

		return {
			getTranslatedDescription,
			value,
			v$,
			getColumnDescription,
			updateDocumentClass,
			showDropdownOverlay,
			update,
		}
	},
});
</script>

<style scoped>

</style>
